'use client';

import { IBlogIndexEntry } from '@/interfaces';
import {
  formatDate,
  getIndexCategory,
  getIndexExcerpt,
  getIndexReadingTime,
  getIndexTags,
  getIndexTitle,
} from '@/lib/blog-utils';
import { Language } from '@/types';
import Image from 'next/image';
import Link from 'next/link';

interface BlogCardProps {
  post: IBlogIndexEntry;
  language: Language;
}

export default function BlogCard({ post, language }: BlogCardProps) {
  const title = getIndexTitle(post, language);
  const category = getIndexCategory(post, language);
  const excerpt = getIndexExcerpt(post, language);
  const tags = getIndexTags(post, language);
  const readingTime = getIndexReadingTime(post, language);

  return (
    <Link
      href={`/${language}/blog/${post.slug}/`}
      className="blog-post card overflow-hidden hover:cursor-pointer block p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200"
    >
      <div>
        {/* Category */}
        {category && (
          <div className="mb-4">
            <span className="post-category inline-block bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300 text-xs px-3 py-1 rounded-full">
              {category}
            </span>
          </div>
        )}

        <h3
          className="text-xl font-semibold mb-3 line-clamp-2 min-h-[3.5rem]"
          style={{ color: 'var(--color-foreground)' }}
        >
          {title}
        </h3>

        <p
          className="mb-4 line-clamp-3 min-h-[4.5rem]"
          style={{ color: 'var(--color-foreground-secondary)' }}
        >
          {excerpt}
        </p>

        {/* Tags */}
        <div className="flex gap-2 mb-4 overflow-hidden">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-block bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Author */}
        <div className="flex items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="relative w-10 h-10 rounded-full overflow-hidden mr-3 flex-shrink-0">
            {post.author_profile_picture ? (
              <Image
                src={post.author_profile_picture}
                alt={post.author}
                fill
                className="object-cover rounded-full"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center rounded-full">
                <span className="text-gray-600 dark:text-gray-300 text-sm">
                  {post.author.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div>
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--color-foreground)' }}
            >
              {post.author}
            </p>
            <p
              className="text-sm"
              style={{ color: 'var(--color-foreground-secondary)' }}
            >
              {formatDate(post.date, language)} · {readingTime}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}
